/**
 * AdvancedMarker Component
 *
 * A React wrapper for google.maps.marker.AdvancedMarkerElement
 * This replaces the deprecated google.maps.Marker
 *
 * @see https://developers.google.com/maps/documentation/javascript/advanced-markers/migration
 */

import { useEffect, useRef, useCallback } from 'react';
import { useGoogleMap } from '@react-google-maps/api';

export interface AdvancedMarkerProps {
  position: google.maps.LatLngLiteral;
  title?: string;
  onClick?: () => void;
  zIndex?: number;
  /** Custom HTML content for the marker. If not provided, uses default pin. */
  content?: HTMLElement | null;
  /** SVG data URL or image URL for simple icon markers */
  iconUrl?: string;
  /** Icon size for scaling */
  iconSize?: { width: number; height: number };
}

export const AdvancedMarker: React.FC<AdvancedMarkerProps> = ({
  position,
  title,
  onClick,
  zIndex,
  content,
  iconUrl,
  iconSize,
}) => {
  const map = useGoogleMap();
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const listenerRef = useRef<google.maps.MapsEventListener | null>(null);

  // Create marker element content from icon URL
  const createIconElement = useCallback((): HTMLElement | undefined => {
    if (!iconUrl) return undefined;

    const img = document.createElement('img');
    img.src = iconUrl;
    if (iconSize) {
      img.style.width = `${iconSize.width}px`;
      img.style.height = `${iconSize.height}px`;
    }
    img.style.cursor = 'pointer';
    return img;
  }, [iconUrl, iconSize]);

  // Initialize marker
  useEffect(() => {
    if (!map) return;

    // Check if AdvancedMarkerElement is available
    if (!google.maps.marker?.AdvancedMarkerElement) {
      console.warn(
        'AdvancedMarkerElement not available. Ensure the "marker" library is loaded.'
      );
      return;
    }

    // Determine marker content
    let markerContent: HTMLElement | undefined;
    if (content) {
      markerContent = content;
    } else if (iconUrl) {
      markerContent = createIconElement();
    }

    // Create AdvancedMarkerElement
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position,
      title,
      zIndex,
      content: markerContent,
    });

    markerRef.current = marker;

    // Add click listener
    if (onClick) {
      listenerRef.current = marker.addListener('click', onClick);
    }

    // Cleanup on unmount
    return () => {
      if (listenerRef.current) {
        google.maps.event.removeListener(listenerRef.current);
        listenerRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
    };
  }, [map]); // Only recreate on map change

  // Update position
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.position = position;
    }
  }, [position]);

  // Update title
  useEffect(() => {
    if (markerRef.current && title !== undefined) {
      markerRef.current.title = title;
    }
  }, [title]);

  // Update zIndex
  useEffect(() => {
    if (markerRef.current && zIndex !== undefined) {
      markerRef.current.zIndex = zIndex;
    }
  }, [zIndex]);

  // Update content/icon
  useEffect(() => {
    if (!markerRef.current) return;

    if (content) {
      markerRef.current.content = content;
    } else if (iconUrl) {
      markerRef.current.content = createIconElement() || null;
    }
  }, [content, iconUrl, iconSize, createIconElement]);

  // Update click handler
  useEffect(() => {
    if (!markerRef.current) return;

    // Remove old listener
    if (listenerRef.current) {
      google.maps.event.removeListener(listenerRef.current);
      listenerRef.current = null;
    }

    // Add new listener
    if (onClick) {
      listenerRef.current = markerRef.current.addListener('click', onClick);
    }
  }, [onClick]);

  // This component doesn't render any DOM - the marker is rendered by Google Maps
  return null;
};

export default AdvancedMarker;
